import {
    ApolloClient,
    ApolloProvider,
    createHttpLink,
    InMemoryCache,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import { Amplify, Auth } from 'aws-amplify'
import _ from 'lodash'
import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'

import App from './App'
import awsExports from './aws-exports'
import reportWebVitals from './reportWebVitals'
import store from './store'

Sentry.init({
    dsn: _.get(process.env, 'REACT_APP_SENTRY_DSN'),
    integrations: [new Integrations.BrowserTracing()],
    debug: _.get(process.env, 'REACT_APP_SENTRY_DEBUG', 'false') === 'true',
})

Amplify.configure(awsExports)

Auth.configure({
    authenticationFlowType: 'USER_SRP_AUTH',
})

const url = awsExports.aws_appsync_graphqlEndpoint

const authLink = setContext(async (_, { headers }) => {
    try {
        const session = await Auth.currentSession()
        const jwt = session.getIdToken().getJwtToken()
        return { headers: { ...headers, Authorization: jwt } }
    } catch {
        return {
            headers: {
                ...headers,
                'x-api-key': process.env.REACT_APP_APPSYNC_API_KEY,
            },
        }
    }
})

const httpLink = createHttpLink({ uri: url })
const link = authLink.concat(httpLink)

const client = new ApolloClient({
    link,
    cache: new InMemoryCache({
        typePolicies: {
            Article: {
                keyFields: ['article_id'],
            },
            Association: {
                keyFields: ['association_id'],
            },
            BeltLevel: {
                keyFields: ['belt_level_id'],
            },
            Dojo: {
                keyFields: ['dojo_id'],
            },
            Event: {
                keyFields: ['event_id'],
            },
            ExperienceLevel: {
                keyFields: ['experience_level_id'],
            },
            MembershipApplication: {
                keyFields: ['membership_application_id'],
            },
            Photo: {
                keyFields: ['photo_id'],
                fields: {
                    // Different queries fetch different subsets of
                    // PhotoSizes (e.g. just `square` for a picker vs.
                    // `small`/`medium`/`large`/`xlarge` for a detail view).
                    // PhotoSizes has no id of its own, so tell the cache to
                    // shallow-merge subsets together instead of replacing
                    // the whole object and losing previously-cached fields.
                    sizes: {
                        merge: true,
                    },
                },
            },
            PhotoAlbum: {
                keyFields: ['photo_album_id'],
            },
            User: {
                keyFields: ['email'],
            },
        },
    }),
})

ReactDOM.render(
    <React.StrictMode>
        <Provider store={store}>
            <Sentry.ErrorBoundary
                fallback={({ error, componentStack, resetError }) => (
                    <React.Fragment>
                        <div>You have encountered an error</div>
                        <code>{error.toString()}</code>
                        <code>{componentStack}</code>
                        <button
                            onClick={() => {
                                resetError()
                            }}
                        >
                            Click here to reset!
                        </button>
                    </React.Fragment>
                )}
                showDialog
            >
                <ApolloProvider client={client}>
                    <App />
                </ApolloProvider>
            </Sentry.ErrorBoundary>
        </Provider>
    </React.StrictMode>,
    document.getElementById('root')
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
